import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import confetti from "canvas-confetti";
import { api, formatApiError } from "@/lib/api";
import { Header } from "@/components/Header";
import { useScopedPath } from "@/lib/scoped";
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  Trophy,
  Receipt,
  ArrowRight,
  Timer,
} from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

function readSessionId() {
  const params = new URLSearchParams(window.location.search);
  return params.get("session_id");
}

// iter149 — parse `<match_date>T<kickoff>` as Europe/London wall time and
// return epoch ms. Copy of the identical helper in MatchesPage.jsx.
function kickoffEpochUk(match_date, kickoff) {
  if (!match_date || !kickoff) return Infinity;
  const ko = String(kickoff).trim();
  const iso = ko.length === 5 ? `${match_date}T${ko}:00` : `${match_date}T${ko}`;
  const asIfLocal = new Date(iso);
  if (Number.isNaN(asIfLocal.getTime())) return Infinity;
  const londonNow = new Date().toLocaleString("en-US", { timeZone: "Europe/London" });
  const browserNow = new Date().toLocaleString("en-US", { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone });
  const deltaMs = new Date(browserNow).getTime() - new Date(londonNow).getTime();
  return asIfLocal.getTime() + deltaMs;
}

// Two staggered bursts from bottom-left and bottom-right for a wide fanned
// arc across the summary card. Kept short (~1.2s) so it doesn't distract.
function fireConfetti() {
  const defaults = {
    startVelocity: 45,
    spread: 65,
    ticks: 200,
    zIndex: 60,
    colors: ["#0038FF", "#F18D49", "#0F766E", "#F5C518", "#E11D2E"],
  };
  confetti({ ...defaults, particleCount: 80, origin: { x: 0.1, y: 0.9 } });
  window.setTimeout(() => {
    confetti({ ...defaults, particleCount: 80, origin: { x: 0.9, y: 0.9 } });
  }, 220);
}

function formatMatchDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  } catch {
    return iso;
  }
}

function formatMoney(amountTotal, currency) {
  if (typeof amountTotal !== "number") return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "gbp").toUpperCase(),
    }).format(amountTotal / 100);
  } catch {
    return `${(amountTotal / 100).toFixed(2)} ${(currency || "gbp").toUpperCase()}`;
  }
}

// iter149 — live countdown to the next kickoff. Ticks once a second and
// gracefully swaps to "Kick-off!" once the timer hits zero.
function NextKickoffCountdown({ kickoffEpochMs, homeTeam, awayTeam }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const diff = Math.max(0, kickoffEpochMs - now);
  const totalSecs = Math.floor(diff / 1000);
  const days = Math.floor(totalSecs / 86400);
  const hours = Math.floor((totalSecs % 86400) / 3600);
  const mins = Math.floor((totalSecs % 3600) / 60);
  const secs = totalSecs % 60;
  const live = diff <= 0;
  const cells = [
    { label: "Days", value: days },
    { label: "Hrs", value: hours },
    { label: "Mins", value: mins },
    { label: "Secs", value: secs },
  ];
  const fixtureLabel =
    homeTeam && awayTeam ? `${homeTeam} vs ${awayTeam}` : "Next fixture";
  return (
    <div
      className="mx-8 mt-6 mb-6 rounded-sm border border-emerald-200 bg-emerald-50/70 px-5 py-4"
      data-testid="next-kickoff-countdown"
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-700 inline-flex items-center gap-1.5">
        <Timer className="h-3 w-3" /> Next kick-off
      </p>
      <p className="mt-1 text-sm font-semibold text-emerald-900" data-testid="next-kickoff-fixture">
        {fixtureLabel}
      </p>
      {live ? (
        <p
          className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-emerald-900 tabular-nums"
          data-testid="next-kickoff-live"
        >
          Kick-off!
        </p>
      ) : (
        <div className="mt-3 grid grid-cols-4 gap-2 sm:gap-3" data-testid="next-kickoff-cells">
          {cells.map((c) => (
            <div
              key={c.label}
              className="rounded-sm bg-white border border-emerald-200 py-2 text-center"
              data-testid={`countdown-${c.label.toLowerCase()}`}
            >
              <p className="font-heading text-2xl sm:text-3xl font-bold tabular-nums text-emerald-900 leading-none">
                {String(c.value).padStart(2, "0")}
              </p>
              <p className="mt-1 text-[9px] font-bold uppercase tracking-[0.2em] text-emerald-700">
                {c.label}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PaymentsSuccessPage() {
  const scope = useScopedPath();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState({
    kind: "polling",
    message: "Confirming your payment…",
  });
  const [nextKickoff, setNextKickoff] = useState(null); // { epochMs, home, away }
  const cancelledRef = useRef(false);
  const confettiFiredRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    const sessionId = readSessionId();
    if (!sessionId) {
      setState({ kind: "error", message: "Missing payment session id" });
      return undefined;
    }

    async function poll(attempt) {
      if (cancelledRef.current) return;
      try {
        const { data } = await api.get(
          `/payments/checkout/status/${encodeURIComponent(sessionId)}`,
        );
        if (data.payment_status === "paid") {
          if (!confettiFiredRef.current) {
            confettiFiredRef.current = true;
            fireConfetti();
          }
          setState({
            kind: "success",
            message:
              "Payment confirmed — you're entered for the match-day.",
            matchDate: data.match_date,
            amountTotal: data.amount_pence ?? null,
            currency: data.currency ?? "gbp",
            sessionId,
          });
          // iter149 — kick off the next-kickoff countdown fetch (fire-and-
          // forget, non-blocking). If nothing's scheduled we just don't
          // render the countdown card.
          (async () => {
            try {
              const { data: up } = await api.get("/matches/upcoming");
              const matches = Array.isArray(up?.matches) ? up.matches : [];
              const earliest = matches
                .map((m) => ({
                  epoch: kickoffEpochUk(m.match_date, m.kickoff),
                  home: m.home_team,
                  away: m.away_team,
                }))
                .filter((m) => Number.isFinite(m.epoch))
                .sort((a, b) => a.epoch - b.epoch)[0];
              if (!cancelledRef.current && earliest) {
                setNextKickoff({
                  epochMs: earliest.epoch,
                  home: earliest.home,
                  away: earliest.away,
                });
              }
            } catch {
              // Silent: countdown is an enhancement, not a failure path.
            }
          })();
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState({
            kind: "error",
            message: "Payment did not complete. Please try again.",
          });
          return;
        }
        if (attempt >= MAX_ATTEMPTS) {
          setState({
            kind: "pending",
            message:
              "Payment is still processing. We'll email you when it confirms.",
          });
          return;
        }
        window.setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
      } catch (err) {
        setState({ kind: "error", message: formatApiError(err) });
      }
    }

    poll(0);
    return () => {
      cancelledRef.current = true;
    };
  }, [location.search]);

  const isSuccess = state.kind === "success";
  const matchDatePretty = formatMatchDate(state.matchDate);
  const amountPretty = formatMoney(state.amountTotal, state.currency);
  const receiptTail = state.sessionId
    ? state.sessionId.slice(-8).toUpperCase()
    : null;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main
        className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24"
        data-testid="payment-success-page"
      >
        {isSuccess ? (
          <div className="rounded-sm bg-white border border-zinc-200 overflow-hidden shadow-sm">
            {/* Celebratory header band */}
            <div className="bg-emerald-600 text-white px-8 py-10 text-center relative overflow-hidden">
              <div className="absolute -top-6 -right-6 opacity-15">
                <Trophy className="h-32 w-32" aria-hidden="true" />
              </div>
              <CheckCircle2
                className="mx-auto h-14 w-14"
                aria-hidden="true"
                data-testid="payment-success-icon"
              />
              <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.28em] text-emerald-100">
                Entry confirmed
              </p>
              <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-bold tracking-tight">
                You&apos;re in.
              </h1>
              <p className="mt-3 text-sm text-emerald-50 max-w-md mx-auto">
                Your predictions for {matchDatePretty || "the next match-day"}{" "}
                are unlocked. Time to lock in the winning card.
              </p>
            </div>

            {/* Receipt / summary */}
            <div className="px-8 py-6 border-b border-zinc-100">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">
                <Receipt className="inline h-3 w-3 mr-1 -mt-0.5" />
                Receipt
              </p>
              <dl className="mt-3 grid grid-cols-2 gap-y-2.5 text-sm">
                {matchDatePretty ? (
                  <>
                    <dt className="text-zinc-500">Match-day</dt>
                    <dd
                      className="text-zinc-950 font-semibold text-right"
                      data-testid="payment-summary-match-date"
                    >
                      {matchDatePretty}
                    </dd>
                  </>
                ) : null}
                {amountPretty ? (
                  <>
                    <dt className="text-zinc-500">Amount paid</dt>
                    <dd
                      className="text-zinc-950 font-semibold text-right tabular-nums"
                      data-testid="payment-summary-amount"
                    >
                      {amountPretty}
                    </dd>
                  </>
                ) : null}
                {receiptTail ? (
                  <>
                    <dt className="text-zinc-500">Reference</dt>
                    <dd className="text-zinc-950 font-mono text-xs text-right tracking-widest">
                      #{receiptTail}
                    </dd>
                  </>
                ) : null}
              </dl>
            </div>

            {nextKickoff && (
              <NextKickoffCountdown
                kickoffEpochMs={nextKickoff.epochMs}
                homeTeam={nextKickoff.home}
                awayTeam={nextKickoff.away}
              />
            )}

            {/* CTAs */}
            <div className="px-8 py-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => navigate(scope("/"), { replace: true })}
                data-testid="payment-back-home"
                className="flex-1 h-12 px-5 rounded-sm bg-[#0038FF] hover:bg-[#002BE6] text-white font-semibold text-sm inline-flex items-center justify-center gap-2"
              >
                Make my predictions
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => navigate(scope("/entries"))}
                data-testid="payment-view-entries"
                className="flex-1 h-12 px-5 rounded-sm border border-zinc-300 hover:bg-zinc-50 text-zinc-950 font-semibold text-sm inline-flex items-center justify-center gap-2"
              >
                <Receipt className="h-4 w-4" />
                View my entries
              </button>
            </div>
          </div>
        ) : (
          <div className="border border-zinc-200 rounded-sm bg-white p-10 text-center">
            {state.kind === "polling" && (
              <Loader2
                className="mx-auto h-10 w-10 text-[#0038FF] animate-spin"
                aria-hidden="true"
                data-testid="payment-polling-spinner"
              />
            )}
            {(state.kind === "error" || state.kind === "pending") && (
              <AlertCircle
                className={`mx-auto h-12 w-12 ${
                  state.kind === "error" ? "text-red-600" : "text-amber-600"
                }`}
                aria-hidden="true"
                data-testid={`payment-${state.kind}-icon`}
              />
            )}
            <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 mt-6">
              {state.kind === "error"
                ? "Something went wrong."
                : state.kind === "pending"
                  ? "Hang tight."
                  : "Processing payment…"}
            </h1>
            <p
              className="mt-3 text-sm text-zinc-600"
              data-testid="payment-status-message"
            >
              {state.message}
            </p>
            <div className="mt-8 flex justify-center gap-3">
              <button
                type="button"
                onClick={() => navigate(scope("/"), { replace: true })}
                data-testid="payment-back-home"
                className="h-10 px-5 rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white font-semibold text-sm"
              >
                Back to predictions
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
